import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Palette, Plus, Pencil, Trash2, Check, Loader2, Star, Sparkles, Globe, Image, Upload } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { toast } from 'sonner';
import { Separator } from '@/components/ui/separator';

const SAMPLE_BAR = [{ name: 'A', v1: 40, v2: 24, v3: 35 }, { name: 'B', v1: 30, v2: 38, v3: 20 }, { name: 'C', v1: 50, v2: 28, v3: 42 }, { name: 'D', v1: 27, v2: 45, v3: 30 }];
const SAMPLE_PIE = [{ name: 'Seg 1', value: 35 }, { name: 'Seg 2', value: 25 }, { name: 'Seg 3', value: 20 }, { name: 'Seg 4', value: 15 }, { name: 'Seg 5', value: 5 }];
const SAMPLE_LINE = [{ x: 1, y1: 10, y2: 20 }, { x: 2, y1: 25, y2: 18 }, { x: 3, y1: 18, y2: 30 }, { x: 4, y1: 35, y2: 25 }, { x: 5, y1: 28, y2: 40 }];

function ThemePreview({ colors, size = 'sm' }) {
  const h = size === 'lg' ? 120 : 60;
  return (
    <div className="flex gap-2">
      <ResponsiveContainer width="50%" height={h}>
        <BarChart data={SAMPLE_BAR}><XAxis dataKey="name" hide /><YAxis hide />
          <Bar dataKey="v1" fill={colors[0] || '#3b82f6'} radius={[2,2,0,0]} />
          <Bar dataKey="v2" fill={colors[1] || '#6366f1'} radius={[2,2,0,0]} />
          <Bar dataKey="v3" fill={colors[2] || '#f59e0b'} radius={[2,2,0,0]} />
        </BarChart>
      </ResponsiveContainer>
      <ResponsiveContainer width="25%" height={h}>
        <PieChart><Pie data={SAMPLE_PIE} cx="50%" cy="50%" innerRadius={size === 'lg' ? 20 : 8} outerRadius={size === 'lg' ? 45 : 22} dataKey="value" strokeWidth={0}>
          {SAMPLE_PIE.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
        </Pie></PieChart>
      </ResponsiveContainer>
      <ResponsiveContainer width="25%" height={h}>
        <LineChart data={SAMPLE_LINE}><Line type="monotone" dataKey="y1" stroke={colors[0]} strokeWidth={2} dot={false} /><Line type="monotone" dataKey="y2" stroke={colors[3] || colors[1]} strokeWidth={2} dot={false} /></LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function ThemeManager() {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defaultThemeId, setDefaultThemeId] = useState('theme_default');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [colors, setColors] = useState(['#3b82f6', '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#06b6d4']);
  const [logoUrl, setLogoUrl] = useState('');
  const [logoPosition, setLogoPosition] = useState('left');
  const [detectedColors, setDetectedColors] = useState([]); // hex[] pulled from the site
  const [colorSources, setColorSources] = useState({}); // hex -> source URL
  const [logoCandidates, setLogoCandidates] = useState([]);
  const [needsLogoPick, setNeedsLogoPick] = useState(false);
  const logoFileRef = React.useRef(null);
  const [saving, setSaving] = useState(false);
  const [aiMode, setAiMode] = useState('describe'); // 'describe' | 'website' | 'image'
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiImage, setAiImage] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const aiFileRef = React.useRef(null);

  const handleAiGenerate = async () => {
    if (aiMode !== 'image' && !aiPrompt.trim()) { toast.error('Enter a description or URL'); return; }
    if (aiMode === 'image' && !aiImage) { toast.error('Upload an image first'); return; }
    setAiGenerating(true);
    try {
      const result = await api.aiGenerateTheme({ mode: aiMode, text: aiPrompt, image_base64: aiImage });
      if (result.colors?.length) {
        setColors(result.colors.slice(0, 12));
        if (result.name && !name) setName(result.name);
        if (result.description && !description) setDescription(result.description);
        setDetectedColors(result.detected_colors || []);
        setColorSources(result.color_sources || {});
        // Website mode may return a logo + candidates.
        if (result.logo_url) setLogoUrl(result.logo_url);
        let candidates = result.logo_candidates || [];
        let needPick = !!result.needs_logo_pick;
        // Image mode: the uploaded image itself is the best logo source — offer
        // it as a candidate so the user can use it as the theme logo.
        if (aiMode === 'image' && aiImage) {
          candidates = [aiImage, ...candidates];
          needPick = true;
        }
        setLogoCandidates(candidates);
        setNeedsLogoPick(needPick);
        if (needPick) {
          toast.success('Palette generated — pick your logo below');
        } else {
          toast.success('AI palette generated!');
        }
      }
    } catch (err) { toast.error(err.message); }
    finally { setAiGenerating(false); }
  };

  const handleAiImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setAiImage(reader.result);
    reader.readAsDataURL(file);
    if (aiFileRef.current) aiFileRef.current.value = '';
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLogoUrl(reader.result);
    reader.readAsDataURL(file);
    if (logoFileRef.current) logoFileRef.current.value = '';
  };

  useEffect(() => {
    Promise.all([api.getThemes(), api.getDefaultTheme()])
      .then(([t, d]) => { setThemes(t); setDefaultThemeId(d?.theme_id || 'theme_default'); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openCreate = () => {
    setEditingTheme(null);
    setName('');
    setDescription('');
    setColors(['#3b82f6', '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#06b6d4']);
    setLogoUrl('');
    setLogoPosition('left');
    setDetectedColors([]);
    setColorSources({});
    setLogoCandidates([]);
    setNeedsLogoPick(false);
    setEditorOpen(true);
  };

  const openEdit = (theme) => {
    setEditingTheme(theme);
    setName(theme.name);
    setDescription(theme.description || '');
    setColors([...theme.colors]);
    setLogoUrl(theme.logo_url || '');
    setLogoPosition(theme.logo_position || 'left');
    setDetectedColors([]);
    setColorSources({});
    setLogoCandidates([]);
    setNeedsLogoPick(false);
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Theme name required'); return; }
    setSaving(true);
    try {
      if (editingTheme) {
        const updated = await api.updateTheme(editingTheme.theme_id, { name, description, colors, accent: colors[0], logo_url: logoUrl, logo_position: logoPosition });
        setThemes(prev => prev.map(t => t.theme_id === editingTheme.theme_id ? updated : t));
      } else {
        const created = await api.createTheme({ name, description, colors, accent: colors[0], logo_url: logoUrl, logo_position: logoPosition });
        setThemes(prev => [...prev, created]);
      }
      setEditorOpen(false);
      toast.success(editingTheme ? 'Theme updated' : 'Theme created');
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (themeId) => {
    try {
      await api.deleteTheme(themeId);
      setThemes(prev => prev.filter(t => t.theme_id !== themeId));
      toast.success('Theme deleted');
    } catch (err) { toast.error(err.message); }
  };

  const handleSetDefault = async (themeId) => {
    try {
      await api.setDefaultTheme(themeId);
      setDefaultThemeId(themeId);
      toast.success('Default theme updated');
    } catch (err) { toast.error(err.message); }
  };

  const setColor = (index, color) => {
    setColors(prev => { const c = [...prev]; c[index] = color; return c; });
  };

  const addColor = () => {
    if (colors.length < 12) setColors(prev => [...prev, '#888888']);
  };

  const removeColor = (index) => {
    if (colors.length > 2) setColors(prev => prev.filter((_, i) => i !== index));
  };

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 text-primary animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Palette className="w-5 h-5 text-primary" />
          <h2 className="text-base font-heading font-semibold text-foreground">Color Themes</h2>
        </div>
        <Button onClick={openCreate} size="sm" className="rounded-lg text-xs"><Plus className="w-3 h-3 mr-1" /> Create Theme</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {themes.map(theme => (
          <div key={theme.theme_id} className={`glass-card rounded-xl p-4 transition-all hover:border-primary/20 ${defaultThemeId === theme.theme_id ? 'ring-1 ring-primary/30' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-foreground">{theme.name}</h3>
                {theme.built_in && <Badge variant="outline" className="text-[7px]">Built-in</Badge>}
                {defaultThemeId === theme.theme_id && <Badge className="text-[7px] bg-primary/20 text-primary border-primary/30">Default</Badge>}
              </div>
              <div className="flex items-center gap-1">
                {defaultThemeId !== theme.theme_id && (
                  <button onClick={() => handleSetDefault(theme.theme_id)} className="p-1 rounded hover:bg-muted/30 text-muted-foreground hover:text-amber-400" title="Set as default"><Star className="w-3 h-3" /></button>
                )}
                {!theme.built_in && (
                  <>
                    <button onClick={() => openEdit(theme)} className="p-1 rounded hover:bg-muted/30 text-muted-foreground hover:text-foreground"><Pencil className="w-3 h-3" /></button>
                    <button onClick={() => handleDelete(theme.theme_id)} className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="w-3 h-3" /></button>
                  </>
                )}
              </div>
            </div>
            {theme.description && <p className="text-[10px] text-muted-foreground mb-2">{theme.description}</p>}
            <div className="flex gap-1 mb-2">
              {theme.colors.slice(0, 8).map((c, i) => <div key={i} className="w-4 h-4 rounded-sm border border-border/30" style={{ background: c }} />)}
            </div>
            <ThemePreview colors={theme.colors} />
          </div>
        ))}
      </div>

      {/* Theme Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Palette className="w-4 h-4 text-primary" /> {editingTheme ? 'Edit Theme' : 'Create Theme'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-muted-foreground">Theme Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Brand Colors" className="rounded-lg bg-muted/50 border-border/50 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Description</Label>
              <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description" className="rounded-lg bg-muted/50 border-border/50 mt-1" />
            </div>

            {/* AI Color Generation */}
            <div className="p-3 rounded-xl bg-indigo-500/5 border border-indigo-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-indigo-400" />
                <p className="text-xs font-semibold text-foreground">AI Color Generator</p>
              </div>
              <div className="flex gap-1 mb-2">
                {[['describe', 'Describe', Palette], ['website', 'Website', Globe], ['image', 'Image', Image]].map(([key, label, Icon]) => (
                  <button key={key} onClick={() => setAiMode(key)} data-testid={`theme-ai-mode-${key}`} className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] transition-all ${aiMode === key ? 'bg-indigo-500/20 text-indigo-400 font-semibold' : 'text-muted-foreground hover:text-foreground'}`}>
                    <Icon className="w-3 h-3" /> {label}
                  </button>
                ))}
              </div>
              {aiMode === 'describe' && (
                <Input value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} data-testid="theme-ai-text-input" placeholder="e.g. Ocean sunset, warm earth tones, minimalist tech..." className="rounded-lg bg-muted/50 border-border/50 text-xs mb-2" />
              )}
              {aiMode === 'website' && (
                <Input value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} data-testid="theme-ai-text-input" placeholder="e.g. stripe.com, airbnb.com, spotify.com..." className="rounded-lg bg-muted/50 border-border/50 text-xs mb-2" />
              )}
              {aiMode === 'image' && (
                <div className="mb-2">
                  <input ref={aiFileRef} type="file" accept="image/*" onChange={handleAiImageUpload} className="hidden" />
                  <button onClick={() => aiFileRef.current?.click()} className="flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed border-border/40 hover:border-indigo-500/40 w-full text-xs text-muted-foreground transition-colors">
                    <Upload className="w-4 h-4" /> {aiImage ? 'Image uploaded — click to change' : 'Upload an image (logo, photo, brand guide...)'}
                  </button>
                  {aiImage && <img src={aiImage} alt="Preview" className="mt-1.5 h-12 rounded-lg border border-border/30 object-cover" />}
                </div>
              )}
              <Button onClick={handleAiGenerate} disabled={aiGenerating} size="sm" data-testid="theme-ai-generate-btn" className="w-full rounded-lg text-xs bg-indigo-500 text-white hover:bg-indigo-600 active:scale-95">
                {aiGenerating ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1.5" />}
                {aiGenerating ? 'Generating...' : 'Generate Palette with AI'}
              </Button>
            </div>

            {/* Logo candidate picker — shown when the AI wasn't sure which
                graphic on the site is the logo. */}
            {needsLogoPick && logoCandidates.length > 0 && (
              <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/20" data-testid="theme-logo-picker">
                <p className="text-[11px] text-foreground font-semibold mb-2">Which graphic is the logo?</p>
                <div className="grid grid-cols-4 gap-2">
                  {logoCandidates.map((u) => (
                    <button
                      key={u}
                      onClick={() => { setLogoUrl(u); setNeedsLogoPick(false); }}
                      className={`aspect-square rounded-lg border-2 p-1 bg-white/90 flex items-center justify-center transition-all ${logoUrl === u ? 'border-primary' : 'border-border/30 hover:border-primary/50'}`}
                      data-testid="theme-logo-candidate"
                      title={u}
                    >
                      <img src={u} alt="candidate" className="max-h-full max-w-full object-contain" />
                    </button>
                  ))}
                </div>
                <button onClick={() => setNeedsLogoPick(false)} className="text-[10px] text-muted-foreground hover:text-foreground mt-2">None of these</button>
              </div>
            )}

            {/* Theme logo — used on report/PDF exports when this theme is applied. */}
            <div>
              <Label className="text-xs text-muted-foreground">Theme Logo <span className="text-muted-foreground/80">(used on report exports)</span></Label>
              <div className="flex gap-2 mt-1 items-center">
                {logoUrl ? <img src={logoUrl} alt="" className="h-9 w-9 object-contain rounded border border-border/30 bg-white/80 p-0.5" data-testid="theme-logo-preview" /> : null}
                <input ref={logoFileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="theme-logo-file" />
                <button onClick={() => logoFileRef.current?.click()} className="px-3 py-1.5 rounded-lg border border-border/30 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 whitespace-nowrap">Choose File</button>
                <Input value={logoUrl} onChange={e => setLogoUrl(e.target.value)} placeholder="or paste logo URL" className="flex-1 rounded-lg bg-muted/50 border-border/50 text-xs" data-testid="theme-logo-url" />
                {logoUrl ? <button onClick={() => setLogoUrl('')} className="text-muted-foreground hover:text-destructive text-xs" title="Remove logo">×</button> : null}
              </div>
              {logoUrl ? (
                <div className="flex items-center gap-2 mt-2" data-testid="theme-logo-position">
                  <span className="text-[10px] text-muted-foreground">Placement on exports/headers:</span>
                  {['left', 'center', 'right'].map(pos => (
                    <button
                      key={pos}
                      onClick={() => setLogoPosition(pos)}
                      className={`px-2 py-0.5 rounded text-[10px] capitalize border ${logoPosition === pos ? 'bg-primary/15 border-primary text-primary' : 'border-border/30 text-muted-foreground hover:border-primary/50'}`}
                      data-testid={`theme-logo-pos-${pos}`}
                    >{pos}</button>
                  ))}
                </div>
              ) : null}
            </div>

            <Separator className="bg-border/30" />

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-muted-foreground">Colors ({colors.length})</Label>
                {colors.length < 12 && <button onClick={addColor} className="text-primary text-[10px] flex items-center gap-0.5"><Plus className="w-3 h-3" /> Add</button>}
              </div>
              {detectedColors.length > 0 && (
                <p className="text-[10px] text-muted-foreground mb-2 flex items-center gap-1.5" data-testid="theme-detection-note">
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> {detectedColors.length} from site</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-400" /> {Math.max(0, colors.length - detectedColors.length)} AI-added</span>
                </p>
              )}
              <div className="grid grid-cols-5 gap-2">
                {colors.map((color, i) => {
                  const fromSite = detectedColors.map(c => c.toLowerCase()).includes(color.toLowerCase());
                  const src = colorSources[color] || colorSources[color.toLowerCase()];
                  return (
                  <div key={i} className="relative group">
                    <div className="w-full aspect-square rounded-lg border-2 border-border/30 overflow-hidden cursor-pointer relative" style={{ background: color }} title={fromSite && src ? `Detected from ${src}` : (fromSite ? 'Detected from site' : 'AI-added')}>
                      <input type="color" value={color} onChange={e => setColor(i, e.target.value)} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                    </div>
                    {detectedColors.length > 0 && (
                      <span className={`absolute top-0.5 left-0.5 w-2 h-2 rounded-full ${fromSite ? 'bg-emerald-500' : 'bg-indigo-400'}`} title={fromSite && src ? `Detected from ${src}` : (fromSite ? 'From site' : 'AI-added')} />
                    )}
                    <p className="text-[8px] text-muted-foreground text-center mt-0.5 font-mono">{color}</p>
                    {colors.length > 2 && <button onClick={() => removeColor(i)} className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-destructive text-white text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">×</button>}
                  </div>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">Preview</Label>
              <div className="glass-card rounded-xl p-3">
                <ThemePreview colors={colors} size="lg" />
              </div>
            </div>

            <Button onClick={handleSave} disabled={saving || !name.trim()} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {editingTheme ? 'Update Theme' : 'Create Theme'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
